'use client';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Input,
  Box,
  HStack,
  Badge,
  Divider,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useState, useEffect, useRef } from 'react';
import { useUser } from '~/hooks/useUser';
import { getAPI } from '~/lib/api';
import { createClient } from '~/lib/providers/supabase/client';

interface SendSMSModalProps {
  isOpen: boolean;
  onClose: () => void;
  queryParams: string;
  brokerCount: number;
  brokerProviderIds?: string[]; // Optional: pre-loaded broker provider IDs
}

interface RecentNotification {
  id: number;
  title: string;
  message: string;
  sent_at: string | null;
  is_sent: boolean;
  recipient_count: number;
  created_at: string;
  sender: {
    id: number;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

export function SendSMSModal({
  isOpen,
  onClose,
  queryParams,
  brokerCount,
  brokerProviderIds,
}: SendSMSModalProps) {
  const [header, setHeader] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState<
    RecentNotification[]
  >([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const { user } = useUser();
  const supabase = createClient();

  // Fetch recent notifications when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchRecentNotifications();
    }
  }, [isOpen]);

  const fetchRecentNotifications = async () => {
    setIsLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(
          `
          id,
          title,
          message,
          sender_id,
          broker_provider_ids,
          channels,
          scheduled_at,
          sent_at,
          is_sent,
          recipient_count,
          created_at,
          sender:users!sender_id (
            id,
            first_name,
            last_name,
            email
          )
        `
        )
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('Error fetching recent notifications:', error);
      } else {
        // Transform the data to handle sender as array from Supabase join
        const transformedData = (data || []).map((notification) => ({
          ...notification,
          sender:
            Array.isArray(notification.sender) && notification.sender.length > 0
              ? notification.sender[0]
              : null,
        })) as RecentNotification[];
        setRecentNotifications(transformedData);
      }
    } catch (error) {
      console.error('Error fetching recent notifications:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSend = () => {
    if (!message.trim()) {
      toast({
        title: 'Meddelande krävs',
        description: 'Du måste skriva ett meddelande',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: 'Fel',
        description: 'Kunde inte identifiera användare',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Show confirmation dialog
    onConfirmOpen();
  };

  const handleConfirmSend = async () => {
    // Check user authentication before starting any process
    if (!user?.id) {
      toast({
        title: 'Fel',
        description: 'Kunde inte identifiera användare',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      onConfirmClose();
      return;
    }

    onConfirmClose();
    setIsSending(true);

    try {
      let recipients: string[];

      // Use pre-loaded broker provider IDs if available
      if (brokerProviderIds && brokerProviderIds.length > 0) {
        recipients = brokerProviderIds;
      } else {
        // Fallback: fetch brokers matching the current search query
        const searchBrokers = await getAPI(
          `/providers/neondatabase/brokers`,
          queryParams
        );

        if (!searchBrokers || searchBrokers.length === 0) {
          toast({
            title: 'Inga mäklare hittades',
            description:
              'Kunde inte hitta några mäklare i den aktuella sökningen',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        const providerIds: (string | undefined)[] = searchBrokers.map(
          (broker: { provider_id?: string }) => broker.provider_id
        );
        recipients = providerIds.filter((id): id is string => !!id);
      }

      // Insert notification directly into Supabase (RLS will apply)
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          title: header.trim() || 'Meddelande från mäklarsökning',
          message: message.trim(),
          sender_id: user.id,
          broker_provider_ids: recipients,
          channels: ['sms'],
          notification_type: 'broker_sms',
          query_params: queryParams,
          recipient_count: recipients.length,
          scheduled_at: new Date().toISOString(),
          is_sent: false,
        })
        .select()
        .single();

      if (notificationError) {
        throw new Error(
          `Failed to create notification: ${notificationError.message}`
        );
      }

      toast({
        title: 'Meddelande schemalagt',
        description: `Meddelandet kommer att skickas till ${recipients.length} mäklare inom kort`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setHeader('');
      setMessage('');
      fetchRecentNotifications(); // Refresh history
      onClose();
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast({
        title: 'Fel vid skickning',
        description:
          error instanceof Error ? error.message : 'Kunde inte skicka SMS',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Skicka SMS till mäklare</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Recent Notifications History */}
            {recentNotifications.length > 0 && (
              <Box>
                <Text fontWeight="medium" fontSize="sm" mb={2}>
                  Senaste meddelanden:
                </Text>
                {isLoadingHistory ? (
                  <HStack justify="center" py={4}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="gray.500">
                      Laddar historik...
                    </Text>
                  </HStack>
                ) : recentNotifications.length === 0 ? (
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    textAlign="center"
                    py={4}
                  >
                    Ingen historik ännu
                  </Text>
                ) : (
                  <Accordion allowMultiple>
                    {recentNotifications.map((notification) => (
                      <AccordionItem key={notification.id} border="none" mb={2}>
                        <AccordionButton
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          bg="gray.50"
                          _hover={{ bg: 'gray.100' }}
                          _expanded={{ bg: 'gray.100', borderBottomRadius: 0 }}
                        >
                          <HStack
                            flex="1"
                            justify="space-between"
                            align="center"
                          >
                            <VStack align="start" spacing={0} flex="1">
                              <Text
                                fontWeight="medium"
                                fontSize="sm"
                                noOfLines={1}
                              >
                                {notification.title}
                              </Text>
                              <HStack
                                fontSize="xs"
                                color="gray.600"
                                spacing={2}
                              >
                                <Text>
                                  {notification.recipient_count} mäklare
                                </Text>
                                <Text>•</Text>
                                <Text>
                                  {new Date(
                                    notification.sent_at ||
                                      notification.created_at
                                  ).toLocaleDateString('sv-SE', {
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </Text>
                              </HStack>
                            </VStack>
                            <HStack spacing={2}>
                              <Badge
                                colorScheme={
                                  notification.is_sent ? 'green' : 'orange'
                                }
                                fontSize="xs"
                              >
                                {notification.is_sent ? 'Skickat' : 'Väntar'}
                              </Badge>
                              <AccordionIcon />
                            </HStack>
                          </HStack>
                        </AccordionButton>
                        <AccordionPanel
                          pb={3}
                          px={3}
                          pt={2}
                          borderWidth="1px"
                          borderTop="none"
                          borderBottomRadius="md"
                          bg="white"
                        >
                          <VStack align="stretch" spacing={2}>
                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight="medium"
                                color="gray.700"
                                mb={1}
                              >
                                Meddelande:
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {notification.message}
                              </Text>
                            </Box>
                            <HStack fontSize="xs" color="gray.500" spacing={3}>
                              {notification.sender && (
                                <>
                                  <Text fontWeight="medium">
                                    Skickat av: {notification.sender.first_name}{' '}
                                    {notification.sender.last_name}
                                  </Text>
                                  <Text>•</Text>
                                </>
                              )}
                              <Text>
                                {new Date(
                                  notification.sent_at ||
                                    notification.created_at
                                ).toLocaleDateString('sv-SE', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </Text>
                            </HStack>
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
                <Divider my={4} />
              </Box>
            )}

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                {brokerCount > 0
                  ? `Meddelandet kommer att skickas till ${brokerCount} mäklare i den aktuella sökningen`
                  : 'Inga mäklare hittades i sökningen'}
              </AlertDescription>
            </Alert>

            <VStack align="stretch" spacing={2}>
              <Text fontWeight="medium" fontSize="sm">
                Rubrik (valfritt):
              </Text>
              <Input
                value={header}
                onChange={(e) => setHeader(e.target.value)}
                placeholder="T.ex. Viktigt meddelande"
                maxLength={100}
              />
              <Text fontSize="xs" color="gray.500">
                {header.length}/100 tecken
              </Text>
            </VStack>

            <VStack align="stretch" spacing={2}>
              <Text fontWeight="medium" fontSize="sm">
                Meddelande:
              </Text>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Skriv ditt meddelande här..."
                rows={6}
                resize="vertical"
              />
              <Text fontSize="xs" color="gray.500">
                {message.length} tecken
              </Text>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={isSending}
          >
            Avbryt
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSend}
            isLoading={isSending}
            isDisabled={isSending || brokerCount === 0}
            loadingText="Skickar..."
          >
            Skicka SMS
          </Button>
        </ModalFooter>
      </ModalContent>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Bekräfta SMS-utskick
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="start" spacing={3}>
                <Text>
                  Du är på väg att skicka ett SMS till{' '}
                  <Text as="span" fontWeight="bold" color="blue.600">
                    {brokerCount} mäklare
                  </Text>
                  .
                </Text>
                {header && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      Rubrik:
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {header}
                    </Text>
                  </Box>
                )}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                    Meddelande:
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {message}
                  </Text>
                </Box>
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Meddelandet kan inte ångras efter att det har skickats.
                  </Text>
                </Alert>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onConfirmClose}
                isDisabled={isSending}
              >
                Avbryt
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleConfirmSend}
                ml={3}
                isLoading={isSending}
                loadingText="Skickar..."
              >
                Skicka SMS
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
}
